import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { AuthContextType, User, AuthTokens, LoginCredentials, UserProfile, ConfirmReplaceRequest } from '../types/auth.types';
import {
  LoginResponse,
  OrganizationSummary,
  isLoginAccess,
  isLoginPreAuth,
} from '../types/multitenancy.types';
import authService from '../services/auth.service';
import { profileService } from '../services/profile.service';
import { hasAnyPermission, hasPermission as hasPermissionUtil } from '../utils/permissions';
import { crmPhone } from '../services/crmphone.service';
import { getVersionOutdated } from '../utils/versionOutdated';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Серверный TTL pre_auth токена — fallback, если в JWT нет `exp`. */
const PRE_AUTH_FALLBACK_TTL_MS = 5 * 60 * 1000;

/** Момент истечения pre_auth токена из JWT `exp`; при неудаче — сейчас + 5 минут. */
function resolvePreAuthExpiry(token: string): number {
  return authService.getPreAuthExpiry(token) ?? Date.now() + PRE_AUTH_FALLBACK_TTL_MS;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * ID активной организации текущего пользователя.
 * Под multi-tenancy active org живёт в JWT и приходит через /auth/me-v2 → currentOrganization.
 * profile.organization_id оставлен как legacy fallback (для single-org инстансов без me-v2).
 */
export const useActiveOrgId = (): number | null => {
  const { currentOrganization, profile } = useAuth();
  return currentOrganization?.id ?? profile?.organization_id ?? null;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationSummary | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<OrganizationSummary[]>([]);
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null);
  const [preAuthExpiresAt, setPreAuthExpiresAt] = useState<number | null>(null);
  const ensureProfilePromiseRef = React.useRef<Promise<void> | null>(null);

  useEffect(() => {
    // Проверяем наличие сохраненных токенов при загрузке
    const init = async () => {
      const savedTokens = authService.getTokens();
      if (savedTokens) {
        setTokens(savedTokens);
        // Предзаполним user для совместимости старого UI; профиль подтянем ниже
        setUser({ username: 'admin', role: 'Administrator' });
        authService.setupAxiosInterceptors(savedTokens.access_token);
        // Профиль и multi-tenancy state грузим параллельно — экономит RTT при холодном
        // старте. meV2 для legacy-токенов вернёт 401 "Token has no organization_id"
        // и fetchWithAuth сам сделает logout + redirect.
        await Promise.all([
          ensureProfile().catch((e) => { void e; }),
          refreshOrgContext().catch((e) => { void e; }),
        ]);
      }
      setIsLoading(false);
    };
    void init();
  }, []);

  // Cross-tab sync: если access_token изменился в другой вкладке (после switch /
  // select-organization), подтянем active org и список через me-v2.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== 'auth_tokens') return;
      // Если значение не изменилось — это re-write того же токена (tabSyncManager
      // иногда так делает при refresh). Не нужно дёргать meV2.
      if (e.newValue === e.oldValue) return;
      if (!authService.getTokens()) return;
      void refreshOrgContext();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Auto-refresh /auth/me-v2 при возврате фокуса/visibility таба.
  // Throttle 60 сек — не дёргаем чаще, защита от частых focus/blur при alt-tab.
  const lastMeRefreshRef = React.useRef<number>(0);
  useEffect(() => {
    const REFRESH_THROTTLE_MS = 60 * 1000;
    const trigger = () => {
      if (document.visibilityState !== 'visible') return;
      if (!authService.getTokens()) return;
      const now = Date.now();
      if (now - lastMeRefreshRef.current < REFRESH_THROTTLE_MS) return;
      lastMeRefreshRef.current = now;
      void refreshOrgContext();
    };
    document.addEventListener('visibilitychange', trigger);
    window.addEventListener('focus', trigger);
    return () => {
      document.removeEventListener('visibilitychange', trigger);
      window.removeEventListener('focus', trigger);
    };
  }, []);

  // Проактивное обновление access-токена каждые 10 минут
  useEffect(() => {
    const intervalMinutes = 10;
    const ms = intervalMinutes * 60 * 1000;
    const id = window.setInterval(async () => {
      try {
        // Не обновляем токен если версия устарела
        if (getVersionOutdated()) {
          console.log('[AuthContext] Пропуск обновления токена - версия устарела');
          return;
        }
        if (authService.getTokens()) {
          await authService.refreshToken().catch(() => undefined);
        }
      } catch (e) { void e; }
    }, ms);
    return () => window.clearInterval(id);
  }, []);

  const ensureProfile = async () => {
    if (!authService.getTokens()) return;
    if (profile) return; // уже загружено
    if (ensureProfilePromiseRef.current) {
      await ensureProfilePromiseRef.current;
      return;
    }
    ensureProfilePromiseRef.current = (async () => {
      try {
        const p = await profileService.getProfile();
        setProfile(p);
        setPermissions(Array.isArray(p.permissions) ? p.permissions : []);
        setUser((prev) => prev ?? { username: p.username, role: p.is_admin ? 'Administrator' : undefined });
      } catch (e) {
        console.error('ensureProfile error', e);
      } finally {
        ensureProfilePromiseRef.current = null;
      }
    })();
    await ensureProfilePromiseRef.current;
  };

  /**
   * Заполнить state по результату meV2 (organization + available_organizations + user).
   * Equality guard через setState(prev → ...) — не триггерит re-render всех consumers
   * AuthContext'а, когда meV2 вернул те же данные (типичный кейс focus/visibility).
   */
  const refreshOrgContext = async () => {
    try {
      const me = await authService.meV2();
      const nextOrg = me.organization ?? null;
      setCurrentOrganization((prev) => {
        if (prev?.id === nextOrg?.id && prev?.code === nextOrg?.code && prev?.name === nextOrg?.name) return prev;
        return nextOrg;
      });
      const nextAvailable = me.available_organizations ?? [];
      setAvailableOrganizations((prev) => {
        if (prev.length !== nextAvailable.length) return nextAvailable;
        for (let i = 0; i < prev.length; i++) {
          if (prev[i].id !== nextAvailable[i].id || prev[i].last_active_at !== nextAvailable[i].last_active_at) {
            return nextAvailable;
          }
        }
        return prev;
      });
      // user может прийти раньше чем profile — синхронизируем минимальные поля
      setUser((prev) => prev ?? {
        username: me.username || 'user',
        role: me.is_admin ? 'Administrator' : undefined,
      });
    } catch (e) {
      console.warn('[AuthContext] refreshOrgContext failed', e);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.loginV2(credentials);

      if (isLoginAccess(result)) {
        // Single-org user или auto-select — обычный flow.
        const saved = authService.getTokens();
        if (saved) setTokens(saved);
        setUser({
          username: credentials.login,
          role: 'Administrator',
        });
        setCurrentOrganization(result.organization);
        setPreAuthToken(null);
        setPreAuthExpiresAt(null);
        // Подтянем профиль и полный список org параллельно — оба нужны на dashboard.
        await Promise.all([
          ensureProfile().catch(() => undefined),
          refreshOrgContext().catch(() => undefined),
        ]);
      } else if (isLoginPreAuth(result)) {
        // Multi-org user — кладём preAuthToken in-memory и список для picker'а.
        setPreAuthToken(result.pre_auth_token);
        setPreAuthExpiresAt(resolvePreAuthExpiry(result.pre_auth_token));
        setAvailableOrganizations(result.available_organizations ?? []);
        setCurrentOrganization(null);
        // tokens НЕ ставим — login ещё не завершён.
      }

      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка авторизации');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const confirmReplace = async (payload: ConfirmReplaceRequest): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authService.confirmReplaceV2(payload);

      if (isLoginAccess(result)) {
        const saved = authService.getTokens();
        if (saved) setTokens(saved);
        setCurrentOrganization(result.organization);
        setPreAuthToken(null);
        setPreAuthExpiresAt(null);
        await Promise.all([
          ensureProfile().catch(() => undefined),
          refreshOrgContext().catch(() => undefined),
        ]);
      } else if (isLoginPreAuth(result)) {
        setPreAuthToken(result.pre_auth_token);
        setPreAuthExpiresAt(resolvePreAuthExpiry(result.pre_auth_token));
        setAvailableOrganizations(result.available_organizations ?? []);
        setCurrentOrganization(null);
      }

      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка подтверждения замены');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const selectOrganization = async (organizationId: number): Promise<void> => {
    if (!preAuthToken) {
      throw new Error('Нет активного pre_auth токена. Войдите снова.');
    }
    setIsLoading(true);
    try {
      const result = await authService.selectOrganization(preAuthToken, organizationId);
      const saved = authService.getTokens();
      if (saved) setTokens(saved);
      setCurrentOrganization(result.organization);
      setPreAuthToken(null);
      setPreAuthExpiresAt(null);
      await Promise.all([
        ensureProfile().catch(() => undefined),
        refreshOrgContext().catch(() => undefined),
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const switchOrganization = async (organizationId: number): Promise<void> => {
    // Не делаем оптимистичных обновлений: дождёмся нового access_token и только потом
    // перезагружаем страницу — это самый безопасный способ инвалидировать все кэши
    // (queries, contexts, WebSocket-подписки), см. handoff §7.4.
    await authService.switchOrganization(organizationId);
    window.location.reload();
  };

  const logout = async () => {
    try {
      try {
        crmPhone.destroy();
      } catch (destroyError) {
        console.warn('Failed to destroy CRM phone widget on logout', destroyError);
      }
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setTokens(null);
      setError(null);
      setProfile(null);
      setPermissions([]);
      setCurrentOrganization(null);
      setAvailableOrganizations([]);
      setPreAuthToken(null);
      setPreAuthExpiresAt(null);
    }
  };

  const hasPermission = (code: string | string[]): boolean => {
    if (Array.isArray(code)) return hasAnyPermission(permissions, code);
    return hasPermissionUtil(permissions, code);
  };

  const value: AuthContextType = {
    user,
    tokens,
    isAuthenticated: !!tokens,
    isLoading,
    login,
    confirmReplace,
    logout,
    error,
    profile,
    permissions,
    ensureProfile,
    hasPermission,
    currentOrganization,
    availableOrganizations,
    preAuthToken,
    preAuthExpiresAt,
    selectOrganization,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
